import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, Search, ShieldCheck, ExternalLink } from "lucide-react";
import Seal from "@/components/Seal";

export default function PublicSchools() {
  const [q, setQ] = useState("");
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/schools/public", { params: q ? { q } : {} });
      setSchools(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSchools(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="schools-public-page">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">ACIS Approved Schools Directory</div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary tracking-tight">
            Find an ACIS-accredited institution.
          </h1>
          <p className="mt-3 max-w-2xl text-slate-600">
            Practitioners credentialed by IBTHM must trace their educational pathway to an ACIS-accredited
            institution or program. Use this directory to verify status before enrolling.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-4 border-slate-200 mb-6">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[260px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="schools-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchSchools()}
                  placeholder="Search institution name"
                  className="pl-9"
                />
              </div>
              <Button onClick={fetchSchools} className="bg-primary text-white hover:bg-[#113A64]" data-testid="schools-search-btn">Search</Button>
            </div>
          </Card>

          <div className="text-sm text-slate-600 mb-4">
            {loading ? "Loading…" : <><span className="font-semibold text-primary">{schools.length}</span> accredited institution{schools.length === 1 ? "" : "s"}</>}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {schools.map((s) => (
              <Card key={s.id} className="p-5 border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid={`school-card-${s.accreditation_number}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-semibold text-primary text-lg leading-snug">{s.name}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {[s.city, s.state, s.country].filter(Boolean).join(", ")}
                    </div>
                  </div>
                  <Seal kind="acis_accredited_member" size="sm" title="ACIS Accredited Member" />
                </div>
                <div className="border-t border-slate-200 pt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Accreditation #</div>
                    <div className="font-mono-data text-sm text-primary mt-0.5">{s.accreditation_number}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Expires</div>
                    <div className="text-sm text-slate-700 mt-0.5">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 capitalize">
                    <ShieldCheck className="h-3 w-3 mr-1" /> {s.accreditation_status}
                  </Badge>
                </div>
                {s.program_offerings?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Programs</div>
                    <div className="flex flex-wrap gap-1.5">
                      {s.program_offerings.slice(0, 4).map((p) => (
                        <Badge key={p} variant="secondary" className="bg-slate-100 text-slate-700 text-[10px]">{p}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            ))}
            {!loading && schools.length === 0 && (
              <div className="col-span-full">
                <Card className="p-10 text-center border-dashed" data-testid="schools-empty">
                  <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <div className="font-heading font-semibold text-primary">
                    {q ? "No schools match your search" : "Directory is currently empty"}
                  </div>
                  <div className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">
                    {q
                      ? "Try adjusting your search terms."
                      : "The ACIS Registry is currently accepting accreditation applications. Approved institutions and programs will appear in the registry following successful accreditation review and approval."}
                  </div>
                </Card>
              </div>
            )}
          </div>

          <div className="mt-12">
            <Card className="p-6 border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-heading font-semibold text-primary">Run an institution?</div>
                <div className="text-sm text-slate-600 mt-1">Apply for institutional or program accreditation through ACIS.</div>
              </div>
              <Link to="/register">
                <Button className="bg-primary text-white hover:bg-[#113A64]" data-testid="schools-apply-btn">
                  Apply for accreditation <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
